// src/components/contact/ContactInfoAndMap.jsx
import React from "react";
import RichTextRenderer from "../common/RichTextRenderer";

import {
  MapPinIcon,
  PhoneIcon,
  PrinterIcon,
  EnvelopeIcon,
  ClockIcon,
} from "@heroicons/react/24/outline";

import { FaWhatsapp } from "react-icons/fa6";

const getWhatsAppLink = (number) => {
  if (!number) return null;
  let cleanedNumber = number.replace(/[\s-()+]/g, "");
  return `https://wa.me/${cleanedNumber}`;
};

const ContactInfoAndMap = ({
  logoUrl,
  logoAlt,
  address,
  addressLink,
  phone,
  fax,
  whatsapp,
  email,
  openingTime,
  mapLat,
  mapLng,
  mapTitle,
  currentLocale = "en",
}) => {
  // ----- Google Map Embed URL -----
  let mapLanguageParam = currentLocale === "zh-Hant" ? "zh-TW" : "en";

  const mapEmbedUrl = `https://maps.google.com/maps?q=${mapLat},${mapLng}&hl=${mapLanguageParam}&z=15&output=embed&iwloc=B`;

  return (
    <div className="container mx-auto px-4 py-8 md:py-12">
      <div className="flex flex-col lg:flex-row md:space-x-8 lg:space-x-12 md:items-stretch">
        <div className="md:w-2/5 lg:w-1/3 mb-8 md:mb-0 flex flex-col space-y-5 md:space-y-6">
          {logoUrl && (
            <img
              src={logoUrl}
              alt={logoAlt || "Company Logo"}
              className="max-h-16 w-auto mb-6 object-contain"
            />
          )}

          {address && (
            <div className="mb-5 flex items-start">
              <MapPinIcon className="w-5 h-5 mr-3 mt-1 text-gray-600 flex-shrink-0" />
              <a
                href={addressLink || "#"}
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-700 hover:text-blue-600 whitespace-pre-line"
              >
                {address}
              </a>
            </div>
          )}
          {phone && (
            <div className="mb-3 flex items-center">
              <PhoneIcon className="w-5 h-5 mr-3 text-gray-600 flex-shrink-0" />
              <a
                href={`tel:${phone.replace(/\s/g, "")}`}
                className="text-gray-700 hover:text-blue-600"
              >
                {phone}
              </a>
            </div>
          )}
          {fax && (
            <div className="mb-3 flex items-center">
              <PrinterIcon className="w-5 h-5 mr-3 text-gray-600 flex-shrink-0" />
              <span className="text-gray-700">{fax}</span>
            </div>
          )}
          {getWhatsAppLink(whatsapp) && (
            <div className="mb-3 flex items-center">
              <FaWhatsapp className="w-5 h-5 mr-3 text-gray-600 flex-shrink-0" />
              <a
                href={getWhatsAppLink(whatsapp)}
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-700 hover:text-blue-600"
              >
                {whatsapp}
              </a>
            </div>
          )}
          {email && (
            <div className="mb-5 flex items-center">
              <EnvelopeIcon className="w-5 h-5 mr-3 text-gray-600 flex-shrink-0" />
              <a
                href={`mailto:${email}`}
                className="text-gray-700 hover:text-blue-600"
              >
                {email}
              </a>
            </div>
          )}
          {openingTime && openingTime.length > 0 && (
            <div className="flex items-start">
              <ClockIcon className="w-5 h-5 mr-3 mt-1 text-gray-600 flex-shrink-0" />
              <div className="text-gray-700">
                <RichTextRenderer
                  content={openingTime}
                  className="prose prose-sm max-w-none [&_p]:my-0.5"
                />
              </div>
            </div>
          )}
        </div>

        <div className="w-3/5">
          {mapLat && mapLng ? (
            <div className="aspect-video lg:aspect-[8/5] w-full rounded-lg shadow-lg overflow-hidden border">
              <iframe
                src={mapEmbedUrl}
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title={mapTitle || "Company Location"}
              ></iframe>
            </div>
          ) : (
            <div className="aspect-video md:aspect-[16/7] w-full rounded-lg shadow-lg bg-gray-200 flex items-center justify-center text-gray-500">
              Map data not available.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContactInfoAndMap;
